import '../styles/Cooking.css';
import React, { useState, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import axios from 'axios';
import { auth, db } from "../config/firebase";
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import CategoryShowcase from '../components/cooking/CategoryShowcase';
import RecipeSearch from '../components/cooking/RecipeSearch';
import UserRecipes from '../components/cooking/UserRecipes';
import RecipeDetailsDialog from '../components/cooking/RecipeDetailsDialog';
import CustomRecipeForm from '../components/cooking/CustomRecipeForm';
import DeleteConfirmationDialog from '../components/cooking/DeleteConfirmationDialog';
import { Hearts } from 'react-loader-spinner';

// Import utilities
import { categories, mealTypes, dietTypes, intoleranceOptions, ensureArray } from '../components/cooking/utils';
import SavedRecipes from "../components/cooking/savedRecipes";

export const Cooking = () => {
    const [activeView, setActiveView] = useState(0);
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recipeDetails, setRecipeDetails] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showRecipeDialog, setShowRecipeDialog] = useState(false);
    const [customMealDialog, setCustomMealDialog] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [deleteRecipeDialog, setDeleteRecipeDialog] = useState(false);
    const [recipeToDelete, setRecipeToDelete] = useState(null);
    const [userMeals, setUserMeals] = useState([]);
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [partnerFavorites, setPartnerFavorites] = useState([]);
    const [lastFetchedTimestamps, setLastFetchedTimestamps] = useState({
        favorites: null,
        userRecipes: null,
        partnerFavorites: null
    });

    const [categoryRecipes, setCategoryRecipes] = useState({
        breakfast: [],
        mainCourse: [],
        dessert: [],
        appetizer: [],
        salad: [],
        soup: []
    });
    const [loadingCategories, setLoadingCategories] = useState({
        breakfast: false,
        mainCourse: false,
        dessert: false,
        appetizer: false,
        salad: false,
        soup: false
    });

    const handleBackToCategories = () => {
        setSelectedCategory(null);
    };

    const loadRecipesWithCaching = async () => {
        try {

            // Set all categories to loading
            const loadingState = {};
            categories.forEach(category => {
                loadingState[category.key] = true;
            });
            setLoadingCategories(loadingState);

            // Fetch recipes from the backend endpoint that handles caching
            const response = await axios.get('https://api.frazza.online/recipes/cached-categories');
            const data = response.data;


            // Update state with the recipes
            setCategoryRecipes(data.recipes);
            setLastUpdated(new Date(data.lastUpdated));

            // Set all categories to not loading
            const notLoadingState = {};
            categories.forEach(category => {
                notLoadingState[category.key] = false;
            });
            setLoadingCategories(notLoadingState);


        } catch (error) {

            // Set all categories to not loading in case of error
            const notLoadingState = {};
            categories.forEach(category => {
                notLoadingState[category.key] = false;
            });
            setLoadingCategories(notLoadingState);
        }
    };

    const fetchFavoriteRecipes = async () => {
        try {
            if (!auth.currentUser) return;

            setLoading(true);
            const userUid = auth.currentUser.uid;

            // Set up real-time listener for favorite recipes
            const favoritesRef = collection(db, 'RazzaRecepies', userUid, 'favoriteRecipes');
            const unsubscribeFavorites = onSnapshot(favoritesRef, (snapshot) => {
                const favorites = [];
                snapshot.forEach(doc => {
                    favorites.push({
                        ...doc.data(),
                        id: doc.id
                    });
                });
                setFavoriteRecipes(favorites);
                setLoading(false);
            }, (error) => {
                console.error("Error listening to favorite recipes:", error);
                setLoading(false);
            });

            return unsubscribeFavorites;
        } catch (error) {
            console.error('Error setting up favorites listener:', error);
            setLoading(false);
        }
    };

// Function to remove a recipe from favorites
    const removeFromFavorites = async (recipe) => {
        try {
            if (!auth.currentUser) return;

            setLoading(true);
            const userUid = auth.currentUser.uid;

            await axios.delete(`https://api.frazza.online/recipes/favorites/${recipe.id}`, {
                params: { userId: userUid }
            });

            // Update the local state
            setFavoriteRecipes(prev => prev.filter(item => item.id !== recipe.id));

        } catch (error) {
            console.error('Error removing recipe from favorites:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchRecipes = async (searchParams) => {
        try {
            setLoading(true);

            const { searchQuery, selectedMealType, selectedDiet, selectedIntolerances } = searchParams;
            const params = new URLSearchParams();

            if (searchQuery) params.append('query', searchQuery);
            if (selectedMealType) params.append('type', selectedMealType);
            if (selectedDiet) params.append('diet', selectedDiet);
            if (selectedIntolerances.length > 0) {
                params.append('intolerances', selectedIntolerances.join(','));
            }

            const response = await axios.get(`https://api.frazza.online/recipes/search?${params}`);

            setRecipes(response.data.results || []);
        } catch (error) {
            setRecipes([]);
            alert('Failed to search recipes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getRecipeDetails = async (id) => {
        try {
            setLoading(true);

            // Check if we already have the recipe in userMeals
            let foundRecipe = userMeals.find(meal => meal.id === id);

            if (foundRecipe) {
                // Use the recipe we already have in memory
                setRecipeDetails(foundRecipe);
                setShowRecipeDialog(true);
                return;
            }

            // If not found in memory, fetch from backend
            const userId = auth.currentUser ? auth.currentUser.uid : null;
            const response = await axios.get(`https://api.frazza.online/recipes/${id}/details`, {
                params: { userId }
            });

            setRecipeDetails(response.data);
            setShowRecipeDialog(true);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const saveCustomMeal = async (customMeal) => {
        try {
            if (!auth.currentUser) {
                return;
            }

            const userId = auth.currentUser.uid;

            const response = await axios.post('https://api.frazza.online/recipes/custom', {
                userId,
                customMeal
            });

            const newRecipe = response.data;

            // Update local state
            setUserMeals([...userMeals, newRecipe]);
            setCustomMealDialog(false);
        } catch (error) {
        }
    };

    const deleteRecipe = async () => {
        try {
            if (!recipeToDelete || !auth.currentUser) return;

            const userUid = auth.currentUser.uid;

            // Delete the recipe through the backend
            await axios.delete(`https://api.frazza.online/recipes/${recipeToDelete.id}`, {
                params: { userId: userUid }
            });

            // Update local state
            setUserMeals(userMeals.filter(meal => meal.id !== recipeToDelete.id));
            setDeleteRecipeDialog(false);
            setRecipeToDelete(null);
        } catch (error) {
        }
    };

    const fetchUserRecipes = async () => {
        // Fetch user's own recipes from backend API instead of Firestore
        try {
            if (!auth.currentUser) return null;

            const userUid = auth.currentUser.uid;

            const params = {
                userId: userUid,
                // If we have a last fetched timestamp, send it to leverage backend caching
                ...(lastFetchedTimestamps.userRecipes
                    ? { lastFetched: lastFetchedTimestamps.userRecipes }
                    : {})
            };

            const { data } = await axios.get('https://api.frazza.online/recipes/user', { params });

            // API returns { hasChanged, lastFetched, data: [...] }
            const serverHasChanged = Boolean(data?.hasChanged);
            const payload = Array.isArray(data?.data) ? data.data : [];

            // Update meals only if this is the first fetch (no lastFetched) or server reports changes
            if (!lastFetchedTimestamps.userRecipes || serverHasChanged) {
                setUserMeals(payload);
            }

            // Update last fetched timestamp (prefer server-provided value)
            const newLastFetched = data?.lastFetched || new Date().toISOString();
            setLastFetchedTimestamps(prev => ({ ...prev, userRecipes: newLastFetched }));

            // No real-time subscription to return
            return null;
        } catch (error) {
            console.error('Error fetching user recipes from API:', error);
            return null;
        }
    };

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
    };


    // Function to fetch partner's favorite recipes
    const fetchPartnerFavorites = async () => {
        try {
            if (!auth.currentUser) return;

            const userUid = auth.currentUser.uid;

            // Get user info to check for partner
            const userInfoDoc = await getDoc(doc(db, "UserInformation", userUid));
            let partnerId = null;
            
            if (userInfoDoc.exists()) {
                const userData = userInfoDoc.data();
                partnerId = userData.inRelationWith;
            }

            if (!partnerId) {
                setPartnerFavorites([]);
                return null;
            }

            // Set up real-time listener for partner's favorite recipes
            const partnerFavoritesRef = collection(db, 'RazzaRecepies', partnerId, 'favoriteRecipes');
            const unsubscribePartnerFavorites = onSnapshot(partnerFavoritesRef, (snapshot) => {
                const partnerFavorites = [];
                snapshot.forEach(doc => {
                    partnerFavorites.push({
                        ...doc.data(),
                        id: doc.id,
                        isPartnerRecipe: true
                    });
                });
                setPartnerFavorites(partnerFavorites);
            }, (error) => {
                console.error("Error listening to partner favorites:", error);
                // If we can't access partner's data, just set empty array
                setPartnerFavorites([]);
            });

            return unsubscribePartnerFavorites;
        } catch (error) {
            console.error('Error setting up partner favorites listener:', error);
            setPartnerFavorites([]);
            return null;
        }
    };

    // Function to set up all real-time listeners
    const setupRealTimeListeners = async () => {
        if (!auth.currentUser) return;

        const unsubscribers = [];
        
        // Set up favorites listener
        const unsubscribeFavorites = await fetchFavoriteRecipes();
        if (unsubscribeFavorites) unsubscribers.push(unsubscribeFavorites);

        // Set up user recipes listener
        const unsubscribeUserRecipes = await fetchUserRecipes();
        if (unsubscribeUserRecipes) unsubscribers.push(unsubscribeUserRecipes);

        // Set up partner favorites listener
        const unsubscribePartnerFavorites = await fetchPartnerFavorites();
        if (unsubscribePartnerFavorites) unsubscribers.push(unsubscribePartnerFavorites);

        return unsubscribers;
    };

    const handleTabChange = (e) => {
        setActiveView(e.index);
        // Update the URL with the new tab index
        const newUrl = `${window.location.pathname}?tab=${e.index}`;
        window.history.pushState({}, '', newUrl);
    };

    // Add useEffect to handle initial tab from URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tabParam = params.get('tab');
        if (tabParam !== null) {
            const tabIndex = parseInt(tabParam);
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
                setActiveView(tabIndex);
            }
        }
    }, []);

    useEffect(() => {
        // Initial data loading
        loadRecipesWithCaching();
        
        // Set up real-time listeners
        let unsubscribers = [];
        
        const initializeListeners = async () => {
            const listeners = await setupRealTimeListeners();
            if (listeners) {
                unsubscribers = listeners;
            }
        };

        initializeListeners();

        // Cleanup function
        return () => {
            unsubscribers.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
        };
    }, []);

    return (
        <div className="cooking-container">
            <h1>Frazzabites</h1>
            <div className="cooking-tabview-center">
                <TabView activeIndex={activeView} onTabChange={handleTabChange}>
                    <TabPanel header="All Categories">
                        {Object.values(loadingCategories).some(isLoading => isLoading) && !selectedCategory ? (
                            <div className="recipes-loading">
                                <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
                                <h3>Preparing delicious recipes...</h3>
                            </div>
                        ) : (
                            <CategoryShowcase
                                selectedCategory={selectedCategory}
                                onBackClick={handleBackToCategories}
                                categoryRecipes={categoryRecipes}
                                loadingCategories={loadingCategories}
                                onRecipeClick={getRecipeDetails}
                                categories={categories}
                                lastUpdated={lastUpdated}
                                onCategoryClick={handleCategoryClick}
                            />
                        )}
                    </TabPanel>

                    <TabPanel header="Search Recipes">
                        <RecipeSearch
                            onSearch={searchRecipes}
                            recipes={recipes}
                            loading={loading}
                            onRecipeClick={getRecipeDetails}
                            mealTypes={mealTypes}
                            dietTypes={dietTypes}
                            intoleranceOptions={intoleranceOptions}
                        />
                    </TabPanel>

                    <TabPanel header="My Recipes">
                        <UserRecipes
                            userMeals={userMeals}
                            onCreateClick={() => setCustomMealDialog(true)}
                            onViewRecipe={(recipeData) => {
                                setRecipeDetails(recipeData);
                                setShowRecipeDialog(true);
                            }}
                            onDeleteRecipe={(meal) => {
                                setRecipeToDelete(meal);
                                setDeleteRecipeDialog(true);
                            }}
                        />
                    </TabPanel>
                    <TabPanel header="Saved">
                        <SavedRecipes
                            loading={loading}
                            favoriteRecipes={favoriteRecipes}
                            partnerFavorites={partnerFavorites}
                            onViewRecipe={(recipe) => getRecipeDetails(recipe.id)}
                            onRemoveRecipe={removeFromFavorites}
                        />
                    </TabPanel>
                </TabView>
            </div>

            {/* Recipe Details Dialog */}
            <RecipeDetailsDialog
                visible={showRecipeDialog}
                onHide={() => {
                    setShowRecipeDialog(false);
                    setRecipeDetails(null);
                }}
                recipeDetails={recipeDetails}
            />

            {/* Custom Recipe Form Dialog */}
            <CustomRecipeForm
                visible={customMealDialog}
                onHide={() => setCustomMealDialog(false)}
                onSave={saveCustomMeal}
                mealTypes={mealTypes}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                visible={deleteRecipeDialog}
                onHide={() => setDeleteRecipeDialog(false)}
                onConfirm={deleteRecipe}
                item={recipeToDelete}
            />
        </div>
    );
};

export default Cooking;
